/**
 * Carousel Module — Swiper-based carousels for all slider sections
 */

import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, EffectCreative } from 'swiper/modules';

/**
 * Initialize all Swiper instances on the page.
 * Each [data-swiper] element becomes a Swiper.
 * Config is read from data attributes.
 */
export function initAllSwipers() {
	document.querySelectorAll( '[data-swiper]' ).forEach( ( el ) => {
		if ( el.swiper ) return; // Already initialized — prevents double-init in editor re-mounts.
		const type = el.dataset.swiper || 'default';
		const perView = parseFloat( el.dataset.swiperPerView ) || 1;
		const space = parseInt( el.dataset.swiperSpace ) || 32;
		const loop = el.dataset.swiperLoop === 'true';
		const autoplay = el.dataset.swiperAutoplay === 'true';

		// Find navigation & pagination within the parent container
		const container = el.closest( '[data-swiper-container]' ) || el.parentElement;
		const prevBtn = container.querySelector( '[data-swiper-prev]' );
		const nextBtn = container.querySelector( '[data-swiper-next]' );
		const counterCurrent = container.querySelector( '[data-swiper-current]' );
		const counterTotal = container.querySelector( '[data-swiper-total]' );

		const config = {
			modules: [ Navigation, Pagination, Autoplay ],
			slidesPerView: perView,
			spaceBetween: space,
			loop: loop,
			grabCursor: true,
			navigation: {
				prevEl: prevBtn,
				nextEl: nextBtn,
			},
			on: {
				init( swiper ) {
					updateCounter( swiper, counterCurrent, counterTotal );
				},
				slideChange( swiper ) {
					updateCounter( swiper, counterCurrent, counterTotal );
				},
			},
		};

		// Type-specific overrides
		if ( type === 'hero' ) {
			config.slidesPerView = 1;
			config.loop = true;
			config.autoplay = { delay: 5000, disableOnInteraction: false };
		}

		if ( type === 'residences' ) {
			config.slidesPerView = 'auto';
			config.spaceBetween = 32;
			config.breakpoints = {
				0: { slidesPerView: 1.2 },
				768: { slidesPerView: 2.5 },
				1280: { slidesPerView: 3.2 },
			};
		}

		// Resident Perks: swipeable card row on tablet/mobile; disabled on
		// desktop (>=1300) where CSS lays the cards out as a static 2-col grid.
		if ( type === 'resident-perks' ) {
			config.slidesPerView = 'auto';
			config.spaceBetween = 24;
			config.breakpoints = {
				// Phones: show one full card + a proportional peek of the next
				// (~15%) so it's clear the row scrolls. Numeric slidesPerView
				// lets Swiper size the slide to the viewport (overriding the
				// fixed 300px card width) — no overflow on narrow screens,
				// unlike a fixed width. Tablet+ keeps the original 300px 'auto'.
				0: { slidesPerView: 1.2, spaceBetween: 16 },
				481: { slidesPerView: 'auto', spaceBetween: 24 },
				1300: { enabled: false },
			};
		}

		// Testimonials carousel is handled by testimonials.js (custom circular arc)
		if ( type === 'testimonials' ) {
			return;
		}

		// UGC slider is handled by custom JS (ugc-slider.js), skip Swiper for it
		if ( type === 'ugc' || type === 'ugc-text' ) {
			return; // Don't init Swiper
		}

		// Initiatives slider uses its own card-flip JS (initiatives-slider.js)
		if ( type === 'thrive-initiatives' ) {
			return;
		}

		if ( autoplay && ! config.autoplay ) {
			config.autoplay = { delay: 4000, disableOnInteraction: false };
		}

		new Swiper( el, config );
	} );
}

function updateCounter( swiper, currentEl, totalEl ) {
	if ( currentEl ) {
		const idx = swiper.realIndex !== undefined ? swiper.realIndex : swiper.activeIndex;
		currentEl.textContent = String( idx + 1 ).padStart( 2, '0' );
	}
	if ( totalEl && ! totalEl._locked ) {
		// Lock total after first set — loop duplicates mess up counting
		totalEl._locked = true;
	}
}
